// ═══════════════════════════════════════════════════════════════════════════════
// ARTIFACT - Recherche Produits (LIVE DATA)
// Affiche les résultats de recherche produits/shop avec grille cliquable
// Coût: 0 crédit - Requête DB uniquement
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Star, Heart, ShoppingCart, ExternalLink,
  Grid, List, Package, Percent, Search, Loader2, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function ArtifactSearchProduits({ 
  initialQuery = '',
  onAddToCart,
  onFavorite 
}) {
  const navigate = useNavigate();
  
  // États
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('pertinence');
  const [showPromo, setShowPromo] = useState(false);
  const [favorites, setFavorites] = useState([]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // FETCH DATA
  // ═══════════════════════════════════════════════════════════════════════════
  
  const fetchProduits = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (showPromo) params.append('promo_only', 'true');
      params.append('sort_by', sortBy);
      params.append('limit', '20');
      
      const response = await fetch(`${API_URL}/api/search/produits?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error('Erreur lors de la recherche');
      }
      
      const data = await response.json();
      setResults(data.results || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Search error:', err);
      setError('Impossible de charger les produits. Réessaie.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, sortBy, showPromo]);
  
  useEffect(() => {
    fetchProduits();
  }, [fetchProduits]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const toggleFavorite = (id) => {
    setFavorites(prev => 
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
    if (onFavorite) onFavorite(id);
  };
  
  const handleAddToCart = (product) => {
    if (onAddToCart) {
      onAddToCart(product);
    }
  };
  
  const handleViewProduct = (product) => {
    navigate(`/shop/produit/${product.slug || product.id}`);
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    fetchProduits();
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU
  // ═══════════════════════════════════════════════════════════════════════════
  
  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-3">
        {/* Barre de recherche */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un produit..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F]"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-3 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2d5a8a] transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </button>
        </form>
        
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="font-semibold text-gray-900">
              {loading ? 'Recherche...' : `${total} produit${total > 1 ? 's' : ''}`}
            </h3>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={fetchProduits}
              disabled={loading}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            
            {/* Toggle promo */}
            <button
              onClick={() => setShowPromo(!showPromo)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                showPromo ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Percent className="w-3 h-3" />
              Promos
            </button>
            
            {/* View mode */}
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'grid' ? 'bg-white shadow-sm' : 'text-gray-500'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-1.5 rounded-md transition-colors ${
                  viewMode === 'list' ? 'bg-white shadow-sm' : 'text-gray-500'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Tri */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Trier par :</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-xs bg-transparent border-none text-gray-700 font-medium cursor-pointer focus:outline-none"
          >
            <option value="pertinence">Pertinence</option>
            <option value="price_asc">Prix croissant</option>
            <option value="price_desc">Prix décroissant</option>
            <option value="rating">Meilleures notes</option>
          </select>
        </div>
      </div>
      
      {/* Résultats */}
      <div className="flex-1 overflow-y-auto p-3">
        {error ? (
          <div className="text-center py-8">
            <p className="text-red-500 mb-2">{error}</p>
            <button
              onClick={fetchProduits}
              className="text-sm text-[#1E3A5F] hover:underline"
            >
              Réessayer
            </button>
          </div>
        ) : loading ? (
          <div className={viewMode === 'grid' ? 'grid grid-cols-2 gap-3' : 'space-y-3'}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-3 animate-pulse">
                <div className={viewMode === 'grid' ? 'aspect-square bg-gray-200 rounded-lg mb-2' : 'flex gap-3'}>
                  {viewMode === 'list' && <div className="w-24 h-24 bg-gray-200 rounded-lg" />}
                  {viewMode === 'list' && <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>}
                </div>
                {viewMode === 'grid' && <>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </>}
              </div>
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-8">
            <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">Aucun produit trouvé</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setShowPromo(false);
              }}
              className="mt-2 text-sm text-[#1E3A5F] hover:underline"
            >
              Réinitialiser la recherche
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 gap-3">
            {results.map((product, idx) => (
              <ProductCardGrid
                key={product.id}
                product={product}
                index={idx}
                isFavorite={favorites.includes(product.id)}
                onToggleFavorite={() => toggleFavorite(product.id)}
                onAddToCart={() => handleAddToCart(product)}
                onView={() => handleViewProduct(product)}
              />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {results.map((product, idx) => (
              <ProductCardList
                key={product.id}
                product={product}
                index={idx}
                isFavorite={favorites.includes(product.id)}
                onToggleFavorite={() => toggleFavorite(product.id)}
                onAddToCart={() => handleAddToCart(product)}
                onView={() => handleViewProduct(product)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRODUCT CARD - GRID
// ═══════════════════════════════════════════════════════════════════════════════

function ProductCardGrid({ product, index, isFavorite, onToggleFavorite, onAddToCart, onView }) {
  const hasPromo = product.promo && product.old_price;
  const discountPercent = hasPromo 
    ? Math.round((1 - product.price / product.old_price) * 100) 
    : 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow group"
    >
      {/* Image */}
      <div className="relative aspect-square bg-gray-100">
        {product.image ? (
          <img
            src={product.image}
            alt={product.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-12 h-12 text-gray-300" />
          </div>
        )}
        
        {/* Promo badge */}
        {hasPromo && discountPercent > 0 && (
          <span className="absolute top-2 left-2 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-lg">
            -{discountPercent}%
          </span>
        )}
        
        {/* Favorite button */}
        <button
          onClick={(e) => { e.stopPropagation(); onToggleFavorite(); }}
          className={`absolute top-2 right-2 p-1.5 rounded-full transition-colors ${
            isFavorite ? 'bg-red-500 text-white' : 'bg-white/80 text-gray-600 hover:bg-white'
          }`}
        >
          <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
        </button>
      </div>
      
      {/* Info */}
      <div className="p-2.5">
        <h4 className="text-sm font-medium text-gray-900 line-clamp-2 leading-tight mb-1">
          {product.title}
        </h4>
        
        {product.rating > 0 && (
          <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
            <Star className="w-3 h-3 text-amber-500 fill-current" />
            {product.rating?.toFixed(1)} ({product.reviews_count || 0})
          </div>
        )}
        
        <div className="flex items-end justify-between">
          <div>
            <span className="text-lg font-bold text-gray-900">
              {typeof product.price === 'number' ? product.price.toFixed(2) : product.price}€
            </span>
            {product.price_unit && (
              <span className="text-xs text-gray-500">/{product.price_unit}</span>
            )}
            {hasPromo && (
              <span className="ml-1 text-xs text-gray-400 line-through">
                {product.old_price?.toFixed(2)}€
              </span>
            )}
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex gap-1.5 mt-2">
          <button
            onClick={onView}
            className="flex-1 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Voir
          </button>
          <button
            onClick={onAddToCart}
            className="flex-1 py-1.5 text-xs text-white bg-[#1E3A5F] rounded-lg hover:bg-[#2d5a8a] transition-colors flex items-center justify-center gap-1"
          >
            <ShoppingCart className="w-3 h-3" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PRODUCT CARD - LIST
// ═══════════════════════════════════════════════════════════════════════════════

function ProductCardList({ product, index, isFavorite, onToggleFavorite, onAddToCart, onView }) {
  const hasPromo = product.promo && product.old_price;
  const discountPercent = hasPromo 
    ? Math.round((1 - product.price / product.old_price) * 100) 
    : 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.03 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow flex"
    >
      {/* Image */}
      <div className="relative w-24 h-24 flex-shrink-0 bg-gray-100">
        {product.image ? (
          <img
            src={product.image}
            alt={product.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-8 h-8 text-gray-300" />
          </div>
        )}
        {hasPromo && discountPercent > 0 && (
          <span className="absolute top-1 left-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded">
            -{discountPercent}%
          </span>
        )}
      </div>
      
      {/* Info */}
      <div className="flex-1 p-2.5 flex flex-col justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-900 line-clamp-1">{product.title}</h4>
          <p className="text-xs text-gray-500">{product.seller?.name || 'Vendeur'}</p>
          <div className="flex items-center gap-2 mt-1">
            {product.rating > 0 && (
              <span className="flex items-center gap-0.5 text-xs text-amber-600">
                <Star className="w-3 h-3 fill-current" />
                {product.rating?.toFixed(1)}
              </span>
            )}
            <span className={`text-xs ${product.stock === 'En stock' ? 'text-emerald-600' : 'text-red-500'}`}>
              {product.stock}
            </span>
          </div>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <span className="font-bold text-gray-900">
              {typeof product.price === 'number' ? product.price.toFixed(2) : product.price}€
            </span>
            {hasPromo && (
              <span className="ml-1 text-xs text-gray-400 line-through">
                {product.old_price?.toFixed(2)}€
              </span>
            )}
          </div>
          <div className="flex gap-1">
            <button
              onClick={onToggleFavorite}
              className={`p-1.5 rounded-lg transition-colors ${
                isFavorite ? 'text-red-500 bg-red-50' : 'text-gray-400 hover:bg-gray-100'
              }`}
            >
              <Heart className={`w-4 h-4 ${isFavorite ? 'fill-current' : ''}`} />
            </button>
            <button
              onClick={onView}
              className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
            <button
              onClick={onAddToCart}
              className="p-1.5 text-white bg-[#1E3A5F] rounded-lg hover:bg-[#2d5a8a] transition-colors"
            >
              <ShoppingCart className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
